import Phaser from "phaser";
import { EMOTICON_KEYS, STARTER_EMOTES, emoticonKeyToFrame } from "../models/season";

export type EmoticonKey = string;

export type EmoteSelection =
  | { kind: "emote"; key: EmoticonKey }
  | { kind: "sticker"; sprayId: number; spriteKey: string };

// Shared static-frame spritesheet every emoticon key indexes into (see sprite-loader.ts).
const EMOTICON_TEXTURE = "emoticons";

const COLUMNS = 4;
const PAGE_SIZE = 12; // 4 columns × 3 rows per page
const PAGE_ROWS = PAGE_SIZE / COLUMNS; // 3
const CELL_SIZE = 52;
const PADDING = 10;
const TITLE_HEIGHT = 28;
const PAGINATION_HEIGHT = 26;

type PickerMode = "emote" | "sticker";

/**
 * EmojiPicker - a Phaser-based popup that shows a paged grid of emoticon sprites
 * (or, in sticker mode, unlocked season sprays) for selection. Positioned above a
 * given anchor point. Closes when a selection is made or when the player taps outside.
 */
export class EmojiPicker {
  private container: Phaser.GameObjects.Container;
  private scene: Phaser.Scene;
  private onSelectCallback: (selection: EmoteSelection) => void;
  private outsideClickHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;
  private currentPage: number = 0;
  private mode: PickerMode = "emote";
  private gridItems: Phaser.GameObjects.GameObject[] = [];
  private prevArrow: Phaser.GameObjects.Text | null = null;
  private nextArrow: Phaser.GameObjects.Text | null = null;
  private pageText: Phaser.GameObjects.Text | null = null;
  private emoteTabText: Phaser.GameObjects.Text | null = null;
  private stickerTabText: Phaser.GameObjects.Text | null = null;
  private pickerWidth: number;
  private pickerHeight: number;
  private totalPages: number = 1;

  constructor(
    scene: Phaser.Scene,
    anchorX: number,
    anchorBottomY: number,
    uiLayer: Phaser.GameObjects.Container | undefined,
    onSelect: (selection: EmoteSelection) => void,
  ) {
    this.scene = scene;
    this.onSelectCallback = onSelect;

    this.pickerWidth = COLUMNS * CELL_SIZE + PADDING * 2;
    this.pickerHeight = PAGE_ROWS * CELL_SIZE + TITLE_HEIGHT + PADDING * 2 + PAGINATION_HEIGHT;

    // Keep picker fully on-screen
    const x = Phaser.Math.Clamp(anchorX, this.pickerWidth / 2 + 4, scene.scale.width - this.pickerWidth / 2 - 4);
    const y = Phaser.Math.Clamp(
      anchorBottomY - this.pickerHeight / 2 - 10,
      this.pickerHeight / 2 + 4,
      scene.scale.height - this.pickerHeight / 2 - 4,
    );

    this.container = scene.add.container(x, y);
    this.container.setScrollFactor(0);
    this.container.setDepth(10200);
    this.container.setSize(this.pickerWidth, this.pickerHeight);

    // Background panel
    const bg = scene.add.graphics();
    bg.fillStyle(0x1a1a2e, 0.97);
    bg.fillRoundedRect(-this.pickerWidth / 2, -this.pickerHeight / 2, this.pickerWidth, this.pickerHeight, 12);
    bg.lineStyle(2, 0x5588ff, 0.9);
    bg.strokeRoundedRect(-this.pickerWidth / 2, -this.pickerHeight / 2, this.pickerWidth, this.pickerHeight, 12);
    this.container.add(bg);

    // Mode tabs: EMOTE / STICKERS
    const tabY = -this.pickerHeight / 2 + TITLE_HEIGHT / 2;
    this.emoteTabText = scene.add
      .text(-this.pickerWidth / 4, tabY, "EMOTE", {
        fontSize: "12px",
        fontFamily: "Anton, sans-serif",
        color: "#99aaff",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.container.add(this.emoteTabText);

    this.stickerTabText = scene.add
      .text(this.pickerWidth / 4, tabY, "STICKERS", {
        fontSize: "12px",
        fontFamily: "Anton, sans-serif",
        color: "#6a6a8a",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.container.add(this.stickerTabText);

    this.emoteTabText.on("pointerdown", () => this.setMode("emote"));
    this.stickerTabText.on("pointerdown", () => this.setMode("sticker"));

    // Pagination bar: prev arrow, page indicator, next arrow
    const barCenterY =
      -this.pickerHeight / 2 + TITLE_HEIGHT + PADDING + PAGE_ROWS * CELL_SIZE + PAGINATION_HEIGHT / 2;

    this.prevArrow = scene.add.text(-this.pickerWidth / 2 + 18, barCenterY, "◄", {
      fontSize: "14px",
      fontFamily: "Arial",
      color: "#ffffff",
    });
    this.prevArrow.setOrigin(0.5);
    this.container.add(this.prevArrow);

    this.nextArrow = scene.add.text(this.pickerWidth / 2 - 18, barCenterY, "►", {
      fontSize: "14px",
      fontFamily: "Arial",
      color: "#ffffff",
    });
    this.nextArrow.setOrigin(0.5);
    this.container.add(this.nextArrow);

    this.pageText = scene.add.text(0, barCenterY, "1 / 1", {
      fontSize: "12px",
      fontFamily: "Anton, sans-serif",
      color: "#aabbff",
    });
    this.pageText.setOrigin(0.5);
    this.container.add(this.pageText);

    // Render initial page
    this.refreshGrid();

    // Container-level hit detection for both grid clicks and pagination
    this.container.setInteractive();
    this.container.on(
      "pointerdown",
      (_pointer: Phaser.Input.Pointer, localX: number, localY: number) => {
        // localX/localY are 0-based from the container's top-left bounding edge
        const gridTop = TITLE_HEIGHT + PADDING;
        const gridBottom = gridTop + PAGE_ROWS * CELL_SIZE;

        // Grid area click
        if (localY >= gridTop && localY < gridBottom) {
          const relX = localX - PADDING;
          const relY = localY - gridTop;
          if (relX >= 0 && relX < COLUMNS * CELL_SIZE && relY >= 0) {
            const col = Math.floor(relX / CELL_SIZE);
            const row = Math.floor(relY / CELL_SIZE);
            const indexOnPage = row * COLUMNS + col;
            const globalIndex = this.currentPage * PAGE_SIZE + indexOnPage;
            const items = this.activeItems();
            if (globalIndex < items.length) {
              const item = items[globalIndex];
              if (this.mode === "emote") {
                this.onSelectCallback({ kind: "emote", key: item as EmoticonKey });
              } else {
                const sprayId = item as number;
                this.onSelectCallback({ kind: "sticker", sprayId, spriteKey: `spray-${sprayId}` });
              }
              this.destroy();
              return;
            }
          }
        }

        // Pagination bar click
        const paginationTop = gridBottom;
        const paginationBottom = paginationTop + PAGINATION_HEIGHT;
        if (localY >= paginationTop && localY < paginationBottom) {
          const third = this.pickerWidth / 3;
          if (localX < third) {
            if (this.currentPage > 0) {
              this.currentPage--;
              this.refreshGrid();
            }
          } else if (localX >= third * 2) {
            if (this.currentPage < this.totalPages - 1) {
              this.currentPage++;
              this.refreshGrid();
            }
          }
        }
      },
    );

    // Close on outside click after a short delay so the opening tap doesn't close it
    this.outsideClickHandler = (pointer: Phaser.Input.Pointer) => {
      if (!this.container || !this.container.scene) {
        this.removeOutsideClickHandler();
        return;
      }
      const halfW = this.pickerWidth / 2;
      const halfH = this.pickerHeight / 2;
      const inBounds =
        pointer.x >= x - halfW &&
        pointer.x <= x + halfW &&
        pointer.y >= y - halfH &&
        pointer.y <= y + halfH;
      if (!inBounds) {
        this.destroy();
      }
    };
    scene.time.delayedCall(80, () => {
      if (this.container && this.container.scene) {
        scene.input.on("pointerdown", this.outsideClickHandler);
      }
    });

    if (uiLayer) {
      uiLayer.add(this.container);
    }
  }

  /** Emote keys the player has unlocked (starts with 4, more unlocked via season rewards). */
  private unlockedEmoteKeys(): EmoticonKey[] {
    const unlocked: string[] | undefined = (window as any).player?.data?.unlockedEmotes;
    if (!Array.isArray(unlocked) || unlocked.length === 0) return [...EMOTICON_KEYS];
    const valid = EMOTICON_KEYS.filter((key) => unlocked.includes(key));
    // Stale pre-rework keys (e.g. "emoticon-yes") won't match anything current -
    // fall back to the starter set rather than showing an empty picker.
    return valid.length > 0 ? valid : [...STARTER_EMOTES];
  }

  /** Season spray ids the player has unlocked — only unlocked ones are ever shown. */
  private unlockedSprayIds(): number[] {
    const unlocked: number[] | undefined = (window as any).player?.data?.unlockedSprays;
    return Array.isArray(unlocked) ? unlocked : [];
  }

  private activeItems(): (EmoticonKey | number)[] {
    return this.mode === "emote" ? this.unlockedEmoteKeys() : this.unlockedSprayIds();
  }

  private setMode(mode: PickerMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.currentPage = 0;
    this.emoteTabText?.setColor(mode === "emote" ? "#99aaff" : "#6a6a8a");
    this.stickerTabText?.setColor(mode === "sticker" ? "#99aaff" : "#6a6a8a");
    this.refreshGrid();
  }

  private refreshGrid(): void {
    // Destroy existing grid items
    this.gridItems.forEach((obj) => obj.destroy());
    this.gridItems = [];

    const items = this.activeItems();
    this.totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    if (this.currentPage >= this.totalPages) this.currentPage = this.totalPages - 1;

    const pageStart = this.currentPage * PAGE_SIZE;
    const pageEnd = Math.min(pageStart + PAGE_SIZE, items.length);

    if (items.length === 0 && this.mode === "sticker") {
      const emptyText = this.scene.add
        .text(0, 0, "No stickers unlocked yet", {
          fontSize: "11px",
          fontFamily: "Arial",
          color: "#6a6a8a",
          align: "center",
          wordWrap: { width: this.pickerWidth - PADDING * 2 },
        })
        .setOrigin(0.5);
      this.container.add(emptyText);
      this.gridItems.push(emptyText);
    }

    for (let i = pageStart; i < pageEnd; i++) {
      const item = items[i];
      const textureKey = this.mode === "emote" ? EMOTICON_TEXTURE : `spray-${item}`;
      const indexOnPage = i - pageStart;
      const col = indexOnPage % COLUMNS;
      const row = Math.floor(indexOnPage / COLUMNS);
      const cellX = -this.pickerWidth / 2 + PADDING + col * CELL_SIZE + CELL_SIZE / 2;
      const cellY = -this.pickerHeight / 2 + TITLE_HEIGHT + PADDING + row * CELL_SIZE + CELL_SIZE / 2;

      const cellBg = this.scene.add.graphics();
      cellBg.fillStyle(0x2a2a4e, 0.85);
      cellBg.fillRoundedRect(-CELL_SIZE / 2 + 3, -CELL_SIZE / 2 + 3, CELL_SIZE - 6, CELL_SIZE - 6, 6);
      cellBg.setPosition(cellX, cellY);
      this.container.add(cellBg);
      this.gridItems.push(cellBg);

      if (this.scene.textures.exists(textureKey)) {
        const sprite =
          this.mode === "emote"
            ? this.scene.add.sprite(cellX, cellY, textureKey, emoticonKeyToFrame(item as EmoticonKey))
            : this.scene.add.image(cellX, cellY, textureKey);
        sprite.setOrigin(0.5);
        sprite.setDisplaySize(CELL_SIZE - 14, CELL_SIZE - 14);
        this.container.add(sprite);
        this.gridItems.push(sprite);
      }
    }

    // Update pagination display
    if (this.pageText) {
      this.pageText.setText(`${this.currentPage + 1} / ${this.totalPages}`);
    }
    if (this.prevArrow) {
      this.prevArrow.setAlpha(this.currentPage > 0 ? 1 : 0.3);
    }
    if (this.nextArrow) {
      this.nextArrow.setAlpha(this.currentPage < this.totalPages - 1 ? 1 : 0.3);
    }
  }

  private removeOutsideClickHandler(): void {
    if (this.outsideClickHandler && this.scene && this.scene.input) {
      this.scene.input.off("pointerdown", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
  }

  destroy(): void {
    this.removeOutsideClickHandler();
    if (this.container && this.container.scene) {
      this.container.destroy();
    }
  }

  isActive(): boolean {
    return !!(this.container && this.container.scene);
  }
}
